import { useInstrumentation } from '#pplx/frontend/framework/instrumentation/useInstrumentation.js';
import { useCallback, useEffect } from 'react';
const THREAD_LOAD_EVENT_STORE_KEY = '__pplxRearmedThreadLoadEventStore';
// This store intentionally outlives individual hook consumers. A terminal
// outcome queued while instrumentation is disabled must survive navigation or
// a sidecar unmount until any later enabled consumer can flush it. Keeping it
// on globalThis also preserves those guarantees across development HMR reloads.
// Framework `once` keys are scoped by event name, so emitted keys intentionally
// omit the event name to keep loaded and failed mutually exclusive. Pending
// keys include it so each framework-scoped event retains its own buffer slot.
// Post-load measurements have no once key: each visit gets its own result.
function getThreadLoadEventStore() {
    const storeGlobal = globalThis;
    storeGlobal[THREAD_LOAD_EVENT_STORE_KEY] ??= {
        emittedKeys: new Set(),
        pendingEvents: new Map(),
    };
    return storeGlobal[THREAD_LOAD_EVENT_STORE_KEY];
}
function getOnceKey(event) {
    return typeof event.once === 'object' && event.once.enabled ? event.once.key : undefined;
}
function flushPendingEvents(track) {
    const { emittedKeys, pendingEvents } = getThreadLoadEventStore();
    const events = Array.from(pendingEvents.values());
    pendingEvents.clear();
    events.forEach((event) => {
        const key = getOnceKey(event);
        if (key !== undefined && emittedKeys.has(key))
            return;
        if (key !== undefined)
            emittedKeys.add(key);
        track(event);
    });
}
export function useRearmedThreadLoadEvent() {
    const { enabled, track } = useInstrumentation();
    useEffect(() => {
        const { pendingEvents } = getThreadLoadEventStore();
        if (!enabled || pendingEvents.size === 0)
            return;
        flushPendingEvents(track);
    }, [enabled, track]);
    return useCallback((event) => {
        const { emittedKeys, pendingEvents } = getThreadLoadEventStore();
        const key = getOnceKey(event);
        if (key !== undefined && emittedKeys.has(key))
            return;
        if (!enabled) {
            const pendingKey = key === undefined ? event : `${event.event}:${key}`;
            if (!pendingEvents.has(pendingKey))
                pendingEvents.set(pendingKey, event);
            return;
        }
        // An enabled consumer can receive an event before its mount effect has
        // flushed outcomes queued by an older consumer. Flush those first so the
        // earliest terminal still wins across event names.
        if (pendingEvents.size > 0)
            flushPendingEvents(track);
        if (key !== undefined && emittedKeys.has(key))
            return;
        if (key !== undefined)
            emittedKeys.add(key);
        track(event);
    }, [enabled, track]);
}
//# sourceMappingURL=useRearmedThreadLoadEvent.js.map